import React, { useState, useEffect } from "react";
import moment from "moment";

const BoekingLogs = ({ boekingId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (boekingId) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boekingId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/admin/boekingen/${boekingId}/logs`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      const data = await response.json();
      setLogs(data.results || []);
      setError(null);
    } catch (err) {
      setError("Fout bij het laden van logs");
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const getActieIcon = (actie) => {
    switch (actie) {
      case 'create':
        return <i className="fas fa-plus-circle text-success"></i>;
      case 'update_status':
        return <i className="fas fa-edit text-primary"></i>;
      case 'update_personen':
        return <i className="fas fa-users text-info"></i>;
      case 'update_klant':
        return <i className="fas fa-user-edit text-info"></i>;
      case 'betaling_verwerkt':
        return <i className="fas fa-credit-card text-success"></i>;
      case 'terugbetaling':
        return <i className="fas fa-undo text-warning"></i>;
      case 'mail_verstuurd':
        return <i className="fas fa-envelope text-primary"></i>;
      case 'omboeken':
        return <i className="fas fa-calendar-alt text-info"></i>;
      case 'delete':
        return <i className="fas fa-trash text-danger"></i>;
      default:
        return <i className="fas fa-history text-muted"></i>;
    }
  };

  const getActieLabel = (actie) => {
    const labels = {
      'create': 'Aangemaakt',
      'update_status': 'Status gewijzigd',
      'update_personen': 'Personen bijgewerkt',
      'update_klant': 'Klant gewijzigd',
      'betaling_verwerkt': 'Betaling verwerkt',
      'terugbetaling': 'Terugbetaling',
      'mail_verstuurd': 'E-mail verstuurd',
      'omboeken': 'Omgeboekt',
      'delete': 'Verwijderd'
    };
    return labels[actie] || actie;
  };

  const formatValue = (value) => {
    if (!value) return 'N/A';

    if (typeof value === 'object') {
      return (
        <div className="font-monospace" style={{ fontSize: '0.85rem' }}>
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="mb-1">
              <span className="text-muted">{key}:</span>{' '}
              <span className="fw-bold">
                {key.includes('datum') && val && !key.includes('_id')
                  ? moment(val).format('DD-MM-YYYY HH:mm')
                  : String(val)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return String(value);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <i className="fas fa-history me-2"></i>
            Activiteiten Log
          </h5>
        </div>
        <div className="card-body text-center">
          <i className="fas fa-spinner fa-spin"></i> Logs laden...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <i className="fas fa-history me-2"></i>
            Activiteiten Log
          </h5>
        </div>
        <div className="card-body">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  const visibleLogs = expanded ? logs : logs.slice(0, 5);

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center bg-light">
        <h5 className="card-title mb-0">
          <i className="fas fa-history me-2 text-primary"></i>
          Activiteiten Log
          <span className="badge bg-primary ms-2">{logs.length}</span>
        </h5>
        {logs.length > 5 && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setExpanded(!expanded)}
          >
            <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} me-1`}></i>
            {expanded ? 'Minder tonen' : `Alle tonen`}
          </button>
        )}
      </div>
      <div className="card-body p-0">
        {logs.length === 0 ? (
          <div className="text-muted text-center py-5">
            <i className="fas fa-inbox fa-3x mb-3 opacity-50"></i>
            <p className="mb-0">Nog geen activiteiten</p>
          </div>
        ) : (
          <div className="list-group list-group-flush">
            {visibleLogs.map((log, index) => (
              <div
                key={log._id}
                className={`list-group-item ${index === 0 ? 'border-start border-primary border-4' : ''}`}
                style={{
                  borderLeft: index === 0 ? '4px solid' : 'none',
                  backgroundColor: index === 0 ? '#f8f9ff' : 'transparent'
                }}
              >
                <div className="d-flex align-items-start">
                  {/* Icon */}
                  <div
                    className="me-3 mt-1"
                    style={{
                      fontSize: '1.5rem',
                      minWidth: '35px',
                      textAlign: 'center'
                    }}
                  >
                    {getActieIcon(log.actie)}
                  </div>

                  {/* Content */}
                  <div className="flex-grow-1">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="mb-1 fw-bold">{getActieLabel(log.actie)}</h6>
                        <p className="mb-0 text-dark">{log.beschrijving}</p>
                      </div>
                      <div className="text-end ms-3" style={{ minWidth: '140px' }}>
                        <div className="text-muted small">
                          <i className="far fa-calendar me-1"></i>
                          {moment(log.timestamp).format('DD-MM-YYYY')}
                        </div>
                        <div className="text-muted small">
                          <i className="far fa-clock me-1"></i>
                          {moment(log.timestamp).format('HH:mm:ss')}
                        </div>
                      </div>
                    </div>

                    {/* Meta info */}
                    <div className="d-flex flex-wrap gap-3 mb-2">
                      <small className="text-muted">
                        <i className="fas fa-user me-1"></i>
                        <strong>{log.user_naam}</strong>
                      </small>
                      {log.ip_adres && (
                        <small className="text-muted">
                          <i className="fas fa-network-wired me-1"></i>
                          {log.ip_adres}
                        </small>
                      )}
                      {log.extra_data?.arrangement_naam && (
                        <small className="text-muted">
                          <i className="fas fa-tag me-1"></i>
                          {log.extra_data.arrangement_naam}
                        </small>
                      )}
                    </div>

                    {/* Details section */}
                    {(log.oude_waarde || log.nieuwe_waarde) && (
                      <div className="mt-3">
                        <details>
                          <summary
                            className="text-primary small fw-bold"
                            style={{cursor: 'pointer', userSelect: 'none'}}
                          >
                            <i className="fas fa-info-circle me-1"></i>
                            Technische details
                          </summary>
                          <div className="mt-2 border rounded p-3 bg-light">
                            <div className="row">
                              {log.oude_waarde && (
                                <div className="col-md-6 mb-2">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="fas fa-arrow-left text-danger me-2"></i>
                                    <strong className="text-danger">Oude waarde</strong>
                                  </div>
                                  <div className="bg-white p-2 rounded border small">
                                    {formatValue(log.oude_waarde)}
                                  </div>
                                </div>
                              )}
                              {log.nieuwe_waarde && (
                                <div className="col-md-6 mb-2">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="fas fa-arrow-right text-success me-2"></i>
                                    <strong className="text-success">Nieuwe waarde</strong>
                                  </div>
                                  <div className="bg-white p-2 rounded border small">
                                    {formatValue(log.nieuwe_waarde)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {logs.length > 5 && !expanded && (
        <div className="card-footer text-center bg-light">
          <button
            className="btn btn-link text-decoration-none"
            onClick={() => setExpanded(true)}
          >
            <i className="fas fa-chevron-down me-1"></i>
            Toon {logs.length - 5} oudere activiteiten
          </button>
        </div>
      )}
    </div>
  );
};

export default BoekingLogs;